// web/src/routes/StudioCreate.tsx
// Task 29: Main create experience — upload → generateRoast → synthesizeSpeech
// → (optionally) generateDoodle → live RoastPlayer preview.
// Additions vs. brief: DOODLE toggle (default off) + mode theme swap (.toast-mode).
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ModeToggle } from "@/components/ModeToggle";
import { AspectToggle } from "@/components/AspectToggle";
import { RoastPlayer } from "@/player/RoastPlayer";
import { validatePdf, renderCover } from "@/lib/pdf";
import { importSlidesAsPdf, importDocsAsPdf, slidesImportConfigured } from "@/lib/googleSlides";
import { runGeneration, type StageId, type SaveInputs } from "@/lib/runGeneration";
import { base64ToObjectUrl } from "@/lib/blobUrl";
import { saveGeneratedRoast } from "@/lib/saveGeneratedRoast";
import { PROCESSING_STAGES } from "@/lib/processingStages";
import type { CreateRequest } from "@/lib/createFlow";
import { canShareVideo, deliverVideo } from "../lib/deliverVideo";
import { recordGeneration, recordGenerationFailed, recordExport, recordExportCancelled } from "@/lib/usage";
import { CHARACTERS } from "@/config/characters";
import { DEFAULT_VOICE, VOICE_OPTIONS } from "@/config/voices";
import { mintRoastId } from "@/lib/ids";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import Gleam from "@/components/Gleam";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { RoastPlayerHandle } from "@/player/types";
import type {
  RoastArtifact,
  Mode,
  Profanity,
  AspectRatio,
} from "@/types/roast";
import { isPaged } from "@/types/roast";
import { PERSISTENCE_ENABLED } from "@/config/features";
import type { CaptionMask, BleepMode } from "@/config/censorship";
import {
  DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE,
  CAPTION_MASK_OPTIONS, BLEEP_MODE_OPTIONS,
  CAPTION_MASK_LABELS, BLEEP_MODE_LABELS,
} from "@/config/censorship";

// CONFIRM: savage maps to BLOCK_NONE server-side only if corp policy permits.
const PROFANITY_LEVELS: Profanity[] = ["clean", "medium", "savage"];

// Opt-in censorship Selects are hidden unless VITE_ENABLE_CENSOR_MODES === "1".
const CENSOR_MODES_ENABLED = import.meta.env.VITE_ENABLE_CENSOR_MODES === "1";

// Progress copy for each shared StageId surfaced by runGeneration's onStage.
const STAGE_COPY: Record<StageId, string> = {
  upload: "Uploading…",
  script: "Writing the script…",
  comprehend: "Studying every slide…",
  render: "Rendering the slides…",
  voice: "Recording the voice…",
  assemble: "Assembling…",
  done: "Done",
};

export default function StudioCreate(): JSX.Element {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const lastStageRef = useRef<StageId>("upload");

  const [sourceMode, setSourceMode] = useState<"photo" | "deck" | "doc">("photo");
  const [roastId, setRoastId] = useState<string>("");
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  const [mode, setMode] = useState<Mode>("roast");
  const [aspect, setAspect] = useState<AspectRatio>("9:16");
  const [profanity, setProfanity] = useState<Profanity>("medium"); // DEFAULT medium
  const [captionMask, setCaptionMask] = useState<CaptionMask>(DEFAULT_CAPTION_MASK);
  const [bleepMode, setBleepMode] = useState<BleepMode>(DEFAULT_BLEEP_MODE);
  const [name, setName] = useState("");
  const [context, setContext] = useState("");
  const [length, setLength] = useState<"microwave" | "deepFry">("deepFry");
  const [voiceId, setVoiceId] = useState<string>(DEFAULT_VOICE.roast);
  const [doodleEnabled, setDoodleEnabled] = useState(false);
  const [doodleKeySlidesEnabled, setDoodleKeySlidesEnabled] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<string>("");
  const [progress, setProgress] = useState(0);
  const [artifact, setArtifact] = useState<RoastArtifact | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [exportMode, setExportMode] = useState<"idle" | "exporting">("idle");
  const [captureSupported, setCaptureSupported] = useState(true);
  // The first-play capture keeps recording ~4.5s past the voice end (music fade-out) —
  // this tracks that window so the button reads "Preparing video…" instead of a
  // tappable "Export Video" that would just start a second, redundant recording.
  const [captureInFlight, setCaptureInFlight] = useState(false);
  const playerRef = useRef<RoastPlayerHandle>(null);
  const [doodleImageUrl, setDoodleImageUrl] = useState<string | null>(null);

  // Raw inputs a later save needs — produced by runGeneration alongside the artifact.
  const [saveInputs, setSaveInputs] = useState<SaveInputs | null>(null);

  const [saving, setSaving] = useState(false);

  // Revoke blob URLs to prevent memory leaks — cleanup runs when the URL
  // changes (revokes the OLD value) and on unmount (revokes the final value).
  useEffect(() => {
    const url = artifact?.audioUrl ?? null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [artifact?.audioUrl]);

  useEffect(() => {
    const url = doodleImageUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [doodleImageUrl]);

  // The photo path sets imageUrl to a local object URL (see handleGeneratePhoto); revoke
  // it on change/unmount. Deck sets imageUrl null; a loaded roast would be a remote URL,
  // on which revokeObjectURL is a harmless no-op.
  useEffect(() => {
    const url = artifact?.imageUrl ?? null;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [artifact?.imageUrl]);

  useEffect(() => {
    const url = previewUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewUrl]);

  // Reset video-export state whenever a new preview artifact appears so the
  // prior blob is discarded and the button returns to "Export Video".
  useEffect(() => { setVideoBlob(null); setExportMode("idle"); setCaptureInFlight(false); }, [artifact]);

  useEffect(() => {
    const url = coverUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [coverUrl]);

  // Fix M1: revoke deck/doc slide image object URLs when the artifact changes
  // (revokes the OLD value) and on unmount — mirrors the previewUrl/coverUrl cleanup
  // above. The paged artifact carries the same keySlides/fillerImages the deck
  // pipeline produced. The artifact?.audioUrl cleanup is handled by the effect above.
  useEffect(() => {
    const a = artifact;
    return () => {
      if (!a || !isPaged(a)) return;
      a.keySlides?.forEach((s) => URL.revokeObjectURL(s.imageUrl));
      a.fillerImages?.forEach((s) => URL.revokeObjectURL(s.imageUrl));
    };
  }, [artifact]);

  const onModeChange = (m: Mode) => {
    setMode(m);
    setVoiceId(DEFAULT_VOICE[m]); // reset to the per-mode default voice
  };

  const switchSource = (m: "photo" | "deck" | "doc") => {
    if (m === sourceMode) return;
    setSourceMode(m);
    setFile(null); setPreviewUrl(null); setCoverUrl(null); setPageCount(null);
    setArtifact(null); setRoastId("");
    // Docs join the deck/rendering side but the portrait/aspect side: decks render
    // 16:9, docs render 9:16 (per-page tall images, smart-zoomed portrait).
    if (m === "deck") setAspect("16:9");
    if (m === "doc") setAspect("9:16");
  };

  const onPickFile = (f: File | null) => {
    setFile(f);
    setPreviewUrl(f ? URL.createObjectURL(f) : null);
    setRoastId(f ? mintRoastId() : "");
  };

  const onPickPdf = async (f: File | null): Promise<boolean> => {
    setFile(null); setCoverUrl(null); setPageCount(null); setArtifact(null); setRoastId("");
    if (!f) { return false; }
    setValidating(true);
    try {
      const res = await validatePdf(f);
      if (!res.ok) {
        setRoastId("");
        toast({ title: "Couldn't read that PDF", description: res.reason, variant: "destructive" });
        return false;
      }
      const cover = await renderCover(f);
      setFile(f);
      setRoastId(mintRoastId());
      setPageCount(res.pageCount ?? null);
      setCoverUrl(URL.createObjectURL(cover.blob));
      return true;
    } catch (e) {
      setRoastId("");
      toast({
        title: "Couldn't read that PDF",
        description: e instanceof Error ? e.message : "Try a different export.",
        variant: "destructive",
      });
      return false;
    } finally {
      setValidating(false);
    }
  };

  const onImportSlides = async () => {
    try {
      const res = await importSlidesAsPdf(undefined, user?.email ?? undefined); // pin Drive consent to the signed-in account
      if (!res) return; // user cancelled — quiet no-op
      const ok = await onPickPdf(res.file); // reuse validate + cover + pageCount + roastId
      if (ok) toast({ title: `Imported "${res.name}"` });
    } catch (e) {
      toast({
        title: "Couldn't import from Google Slides",
        description: e instanceof Error ? e.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const onImportDocs = async () => {
    try {
      const res = await importDocsAsPdf(undefined, user?.email ?? undefined); // pin Drive consent to the signed-in account
      if (!res) return; // user cancelled — quiet no-op
      const ok = await onPickPdf(res.file); // reuse validate + cover + pageCount + roastId
      if (ok) toast({ title: `Imported "${res.name}"` });
    } catch (e) {
      toast({
        title: "Couldn't import from Google Docs",
        description: e instanceof Error ? e.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const canGenerate = useMemo(
    () => !!file && !!user && !busy && (sourceMode === "photo" || pageCount != null),
    [file, user, busy, sourceMode, pageCount],
  );

  // One flow for photo AND deck/doc — build a CreateRequest from component state and
  // hand off to the shared runGeneration pipeline (single source of truth, DRY). The
  // typed onStage events map to the existing progress copy + a coarse band-start percent.
  const handleGenerate = async () => {
    if (!file || !user) return;
    if (sourceMode !== "photo" && pageCount == null) return;
    setBusy(true);
    setArtifact(null);
    setSaveInputs(null);
    setDoodleImageUrl(null);
    const type = sourceMode;
    const req: CreateRequest = {
      source: file,
      detectedType: type,
      mode,
      profanity,
      doodle: type === "photo" ? doodleEnabled : doodleKeySlidesEnabled,
      pageCount: pageCount ?? 0,
      voiceId,
      // Use the roastId hoisted from onPickFile/onPickPdf; mint a fresh one if empty.
      roastId: roastId || mintRoastId(),
      aspect,
      coverBlob: new Blob(),
      name: name.trim() || null,
      context: context.trim() || null,
      length,
      captionMask,
      bleepMode,
    };
    try {
      const result = await runGeneration(req, {
        user: { uid: user.uid, email: user.email },
        onStage: (e) => {
          lastStageRef.current = e.stage;
          setStage(STAGE_COPY[e.stage]);
          const node = PROCESSING_STAGES[type].find((n) => n.stageIds.includes(e.stage));
          if (node) setProgress(Math.round(node.band[0]));
        },
      });
      setArtifact(result.artifact);
      setSaveInputs(result.saveInputs);
      recordGeneration({ email: user.email, uid: user.uid, mode: req.mode, source: type });
      // Doodle preview URL (photo path only) — decode the retained base64 for the player.
      if (result.saveInputs.source === "photo" && result.saveInputs.doodleBase64) {
        setDoodleImageUrl(base64ToObjectUrl(result.saveInputs.doodleBase64, result.saveInputs.doodleMimeType ?? "image/png"));
      }
      setStage("Done");
      setProgress(100);
    } catch (e) {
      recordGenerationFailed({ source: type, stage: lastStageRef.current });
      toast({
        title: "Generation failed",
        description: e instanceof Error ? e.message : "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!artifact || !saveInputs || !user) return;
    setSaving(true);
    try {
      // Persist the live aspect (photo can toggle it after generation; deck/doc are
      // forced to their source aspect, which previewAspect already reflects).
      const id = await saveGeneratedRoast(
        { artifact: { ...artifact, aspect: previewAspect ?? artifact.aspect }, saveInputs },
        { uid: user.uid, email: user.email },
      );
      navigate(`/r/${id}`);
    } catch (e) {
      toast({
        title: "Save failed",
        description: e instanceof Error ? e.message : "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const character = CHARACTERS[mode];

  // The effective preview/player aspect: decks are always 16:9, docs are always
  // 9:16 (portrait pages, smart-zoomed) — photo alone follows the live toggle.
  // NOT a blanket isPaged swap: deck and doc diverge here (isPaged is used below
  // only for the deck-shaped PROPS — imageUrl/detectedObjects/keySlides/etc. — which
  // both share).
  const previewAspect: AspectRatio | null = artifact
    ? artifact.sourceType === "deck"
      ? "16:9"
      : artifact.sourceType === "doc"
        ? "9:16"
        : aspect
    : null;

  // Apply .toast-mode class to <main> for the mode theme swap.
  // The CSS in globals.css defines .toast-mode to override --mode-color tokens.
  const mainClass = [
    "mx-auto grid max-w-5xl gap-6 px-6 py-8 md:grid-cols-2",
    mode === "toast" ? "toast-mode" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className={mainClass}>
      <Card className="space-y-5 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Create a {character.label}</h1>
          <ModeToggle mode={mode} onChange={onModeChange} />
        </div>

        {/* Source toggle */}
        <div className="flex gap-2" role="group" aria-label="Source">
          <Button
            type="button"
            variant={sourceMode === "photo" ? "default" : "secondary"}
            onClick={() => switchSource("photo")}
          >
            Photo
          </Button>
          <Button
            type="button"
            variant={sourceMode === "deck" ? "default" : "secondary"}
            onClick={() => switchSource("deck")}
          >
            Deck
          </Button>
          <Button
            type="button"
            variant={sourceMode === "doc" ? "default" : "secondary"}
            onClick={() => switchSource("doc")}
          >
            Doc
          </Button>
        </div>

        {/* File input — shared PDF input for deck AND doc; photo gets an image input */}
        {sourceMode !== "photo" ? (
          <div className="space-y-2">
            <Label htmlFor="deck">{sourceMode === "doc" ? "Document (PDF)" : "Deck (PDF)"}</Label>
            <Input
              id="deck"
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => onPickPdf(e.target.files?.[0] ?? null)}
            />
            {slidesImportConfigured() && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={validating}
                onClick={sourceMode === "doc" ? onImportDocs : onImportSlides}
              >
                {sourceMode === "doc" ? "Import from Google Docs" : "Import from Google Slides"}
              </Button>
            )}
            {validating && (
              <p className="text-sm text-muted-foreground">
                Reading the {sourceMode === "doc" ? "document" : "deck"}…
              </p>
            )}
            {pageCount !== null && (
              <p className="text-sm text-muted-foreground">
                Read {pageCount} {sourceMode === "doc" ? "pages" : "slides"}
              </p>
            )}
            {coverUrl && (
              <div
                data-testid="deck-cover-wrap"
                data-aspect={sourceMode === "doc" ? "9:16" : "16:9"}
                className="mt-2 overflow-hidden rounded-md"
                style={{ aspectRatio: sourceMode === "doc" ? "9 / 16" : "16 / 9", maxHeight: "12rem" }}
              >
                <img
                  src={coverUrl}
                  alt={sourceMode === "doc" ? "document cover" : "deck cover"}
                  className="h-full w-full object-contain"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="photo">Photo</Label>
            <Input
              id="photo"
              ref={fileInput}
              type="file"
              accept="image/*"
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            />
            {previewUrl && (
              <img
                src={previewUrl}
                alt="source preview"
                className="mt-2 max-h-48 rounded-md object-contain"
              />
            )}
          </div>
        )}

        {/* Optional name */}
        <div className="space-y-2">
          <Label htmlFor="name">Name (optional)</Label>
          <Input
            id="name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Who is this?"
          />
        </div>

        {/* Optional context */}
        <div className="space-y-2">
          <Label htmlFor="context">Context (optional)</Label>
          <Textarea
            id="context"
            value={context}
            maxLength={800}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Anything the roast should mock…"
          />
        </div>

        {/* Spice + Length */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Spice</Label>
            <Select
              value={profanity}
              onValueChange={(v) => setProfanity(v as Profanity)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROFANITY_LEVELS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p === "clean" ? "Clean" : p === "medium" ? "Medium" : "Savage"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Length</Label>
            <Select
              value={length}
              onValueChange={(v) => setLength(v as "microwave" | "deepFry")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="microwave">Microwave (15s)</SelectItem>
                <SelectItem value="deepFry">Deep Fry (30s)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Opt-in censorship modes — hidden unless VITE_ENABLE_CENSOR_MODES === "1".
            Both default to today's behavior, so normal users see zero change. */}
        {CENSOR_MODES_ENABLED && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Caption mask</Label>
              <Select value={captionMask} onValueChange={(v) => setCaptionMask(v as CaptionMask)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAPTION_MASK_OPTIONS.map((m) => (
                    <SelectItem key={m} value={m}>{CAPTION_MASK_LABELS[m]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Bleep mode</Label>
              <Select value={bleepMode} onValueChange={(v) => setBleepMode(v as BleepMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BLEEP_MODE_OPTIONS.map((m) => (
                    <SelectItem key={m} value={m}>{BLEEP_MODE_LABELS[m]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Voice */}
        <div className="space-y-2">
          <Label>Voice</Label>
          <Select value={voiceId} onValueChange={setVoiceId}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VOICE_OPTIONS[mode].map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Format (aspect ratio) — photo only; decks are always 16:9 */}
        {sourceMode === "photo" && (
          <div className="space-y-2">
            <Label>Format</Label>
            <div><AspectToggle aspect={aspect} onChange={setAspect} /></div>
          </div>
        )}

        {/* Doodle toggle — photo only */}
        {sourceMode === "photo" && (
          <div className="flex items-center gap-3">
            <Switch
              id="doodle-toggle"
              checked={doodleEnabled}
              onCheckedChange={setDoodleEnabled}
            />
            <Label htmlFor="doodle-toggle">Add Doodle</Label>
          </div>
        )}

        {/* Doodle key slides toggle — deck only */}
        {sourceMode === "deck" && (
          <div className="flex items-center gap-3">
            <Switch
              id="deck-doodle-toggle"
              checked={doodleKeySlidesEnabled}
              onCheckedChange={setDoodleKeySlidesEnabled}
            />
            <Label htmlFor="deck-doodle-toggle">Doodle key slides</Label>
          </div>
        )}

        {/* Progress */}
        {busy && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground">{stage}</p>
          </div>
        )}

        <Button disabled={!canGenerate} onClick={handleGenerate} className="w-full">
          {busy ? "Generating…" : `Generate ${character.label}`}
        </Button>

        {/* Persistence UI hidden for the tester deploy (no data retention) — see config/features. */}
        {PERSISTENCE_ENABLED && artifact && (
          <Button
            disabled={saving}
            onClick={handleSave}
            className="w-full"
            variant="secondary"
          >
            {saving ? "Saving…" : "Save & Share"}
          </Button>
        )}
      </Card>

      {/* Preview panel */}
      <Card className="flex flex-col items-center justify-center overflow-hidden p-0">
        {artifact ? (
          <>
            <div
              className="relative w-full overflow-hidden rounded-md"
              style={{
                aspectRatio: previewAspect === "9:16" ? "9 / 16" : "16 / 9",
                maxHeight: "70vh",
                margin: "0 auto",
                ...(previewAspect === "9:16" ? { maxWidth: "min(100%, 39.4vh)" } : {}),
              }}
            >
              <RoastPlayer
                ref={playerRef}
                mode={artifact.mode}
                script={artifact.script}
                audioUrl={artifact.audioUrl!}
                imageUrl={isPaged(artifact) ? null : artifact.imageUrl}
                wordTimings={artifact.wordTimings}
                detectedObjects={isPaged(artifact) ? [] : artifact.detectedObjects}
                isCharacterFullscreen={artifact.isCharacterFullscreen}
                aspect={previewAspect ?? aspect}
                doodleImageUrl={isPaged(artifact) ? undefined : (doodleImageUrl ?? undefined)}
                audio={artifact.audio}
                captionMask={artifact.captionMask}
                bleepMode={artifact.bleepMode}
                captureFirstPlay
                onCaptureComplete={(blob) => {
                  setVideoBlob(blob);
                  setExportMode("idle");
                }}
                onCaptureStatusChange={(s) => {
                  if (s === "unsupported") { setCaptureSupported(false); setExportMode("idle"); }
                  if (s === "aborted") setExportMode("idle");
                  if (s === "recording") setCaptureInFlight(true);
                  if (s === "complete" || s === "aborted" || s === "unsupported") setCaptureInFlight(false);
                }}
                {...(isPaged(artifact)
                  ? { keySlides: artifact.keySlides, fillerImages: artifact.fillerImages, slideSchedule: artifact.slideSchedule }
                  : {})}
              />
              {exportMode === "exporting" && (
                <div
                  data-testid="export-overlay"
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/70 text-white"
                >
                  <span>Exporting video — keep the screen on and stay on this page</span>
                  <Button
                    data-testid="export-cancel"
                    variant="secondary"
                    onClick={() => { playerRef.current?.cancelExport(); setExportMode("idle"); }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            {captureSupported && exportMode !== "exporting" && (
              <div className="p-3">
                {videoBlob ? (
                  <Button
                    data-testid="download-video"
                    className="relative overflow-hidden bg-red-600 text-white hover:bg-red-500"
                    onClick={() => {
                      const source = artifact.sourceType ?? "photo";
                      const isTouch = canShareVideo();
                      void deliverVideo(videoBlob, `${artifact.mode}-${source}-${Date.now()}.mp4`)
                        .then((outcome) => {
                          if (outcome === "cancelled") recordExportCancelled({ source, isTouch });
                          else recordExport({ email: user?.email ?? null, uid: user?.uid ?? null, outcome, source, isTouch });
                        });
                    }}
                  >
                    {canShareVideo() ? "Share Video" : "Download Video"}
                    <Gleam className="rounded-full" opacity={0.45} />
                  </Button>
                ) : captureInFlight ? (
                  <Button data-testid="preparing-video" disabled>
                    Preparing video…
                  </Button>
                ) : (
                  <Button
                    data-testid="export-video"
                    onClick={() => { setExportMode("exporting"); playerRef.current?.exportVideo(); }}
                  >
                    Export Video
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="p-10 text-center text-muted-foreground">
            Your live preview will appear here.
          </p>
        )}
      </Card>
    </main>
  );
}
